import {
  HomeworkPagination,
  IconWrapper,
  IQuestionStatus,
  IStyles,
  LinkButton,
  Loader,
  NoContentCard,
  QuestionContainerWithSolution,
  SelectMenu,
  deserify,
  getLastSignificantState,
  pxToRem,
  pxTovW,
  roundNumberTo2Decimal,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Question } from '@protos/content_management/content.db_pb';
import {
  AssessmentEnum,
  AttemptStats,
  AttemptTypeEnum,
  MarkingSchemeEnum,
  ModeOfConductionEnum,
  QAttemptResultEnum,
  SessionStatusEnum,
  SubmissionType,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { HWStudentResponse } from '@protos/learning_management/lms.hw.student.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  HOMEWORK_STUDENT_DASHBOARD,
  HOMEWORK_VIEW_RECOMMENDATION,
} from '../../../routeHandling/RoutesNomenclature';
import {
  setActiveHomeworkContent,
  setActiveHomeworkStudentResponse,
  setSelectedHomeworkData,
} from '../reducer/homework.slice';

import { interactionEvent } from '../../Auth/Login/login_logout.events';
import {
  aiHomeworkReviewCloseEvent,
  aiHomeworkReviewOpenEvent,
} from '../utils_homework/homework.events';
import { CustomHomeworkDisplay } from './component/CustomHomeworkDisplay';
import { OfflineEndedCustomHw } from './component/OfflineEndedCustomHw';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import ViewStudentReportButton from './component/ViewStudentReportButton';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { updateAssessmentContentWithMarks } from '../utils_homework/homeworkUtil';
import { AssessmentStudentResponseV2 } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { getSubjectNameFromEnum } from '../utils_assessment/assessmentUtils';
import {
  setActiveAssessmentContent,
  setActiveAssessmentStudentResponse,
} from '../MultiSubjectFormativeAssessment/reducer/multisubjectFA.slice';

const styles: IStyles = {
  root: {
    width: '100vw',
    boxSizing: 'border-box',
    p: {
      xs: `${pxToRem(15)} ${pxToRem(9)}`,
      md: pxToRem(20),
      lg: `${pxTovW(60)} ${pxTovW(240)}`,
    },
    display: 'flex',
    justifyContent: 'space-between',
    m: 0,
  },

  heading: {
    mb: { xs: pxToRem(20), md: pxTovW(20) },
  },

  subHeadingBox: {
    padding: {
      xs: `${pxToRem(14)} ${pxToRem(41)}`,
      md: `${pxTovW(36)} ${pxTovW(0)}`,
    },
    display: 'flex',
    gap: { md: pxTovW(17) },
    justifyContent: { xs: 'space-between', md: 'flex-start' },
    alignItems: 'center',
  },
  subHeading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  questionBox: {
    margin: 'auto',
    width: '100%',
    padding: {
      xs: `${pxToRem(20)} ${pxToRem(10)}`,
      // md: `${pxTovW(20)} ${pxTovW(40)}`,
    },
    boxSizing: 'border-box',
  },

  overallSummaryWrap: {
    width: '100%',
    mt: { xs: pxToRem(18), md: pxTovW(26) },
    px: { xs: pxToRem(4), md: 0 },
  },

  overallSummaryList: {
    mt: { xs: pxToRem(10), md: pxTovW(14) },
    maxHeight: { xs: pxToRem(320), md: pxTovW(520) },
    overflowY: 'auto',
    pr: { xs: pxToRem(4), md: pxTovW(6) },
  },

  summaryCard: {
    position: 'relative',
    mb: { xs: pxToRem(10), md: pxTovW(12) },
    p: {
      xs: `${pxToRem(20)} ${pxToRem(20)}`,
      md: `${pxTovW(25)} ${pxTovW(30)}`,
    },
    borderRadius: { xs: pxToRem(12), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    boxShadow: `0px 0px ${pxTovW(10)} #0000001F`,
  },

  summaryChip: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#CCE8FF',
    color: '#155D8A',
    borderBottomLeftRadius: { xs: pxToRem(8), md: pxTovW(8) },
    borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(10) },
    px: { xs: pxToRem(10), md: pxTovW(10) },
    py: { xs: pxToRem(3), md: pxTovW(3) },
    maxWidth: { xs: '68%', md: '62%' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  summaryMetricsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    pt: { xs: pxToRem(18), md: pxTovW(22) },
  },
};
interface IProps {
  homeworkFlow: boolean;
}
export const CompletedHw = ({ homeworkFlow }: IProps) => {
  console.log('homeworkFlow', homeworkFlow);
  //^ HwPagination
  const location = useLocation();
  const homework_id =
    new URLSearchParams(location.search).get('assessmentId') || undefined;
  const subjectTabsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    isPointerDown: false,
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: -1,
  });
  const active_homework_content = deserify(
    useAppSelector((state) =>
      homeworkFlow
        ? state.homework.active_homework_content
        : state.multisubjectFA.active_assessment_content
    )
  );
  const active_homework_student_response = deserify(
    useAppSelector((state) =>
      homeworkFlow
        ? state.homework.active_homework_student_response
        : state.multisubjectFA.active_assessment_student_response
    )
  );
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );
  const [selectedAttemptInfo, setSelectedAttemptInfo] = useState<
    AttemptStats | undefined
  >();
  const activeHomeworkContent =
    active_homework_content && homework_id
      ? active_homework_content[homework_id]
      : undefined;
  const activeHomeworkStudentResponse =
    active_homework_student_response && homework_id
      ? active_homework_student_response[homework_id]
      : undefined;
  const [selectedSectionNo, setSelectedSectionNo] = useState<number>();
  const taskQuestionInfo =
    activeHomeworkContent?.questionsSequenceInfo?.taskQuestionInfo || [];
  const multiSubjectSections =
    activeHomeworkContent?.questionsSequenceInfo?.multiSubjectSections || [];
  const taskQuestionInfoSubjectWise =
    multiSubjectSections
      .find((section) => section.sectionNo === selectedSectionNo)
      ?.multiSubjectSubSections.map(
        (subSection) => subSection.subSectionMetaData
      )
      .flat() || [];
  const sortedQuestionIds = (
    selectedSectionNo ? taskQuestionInfoSubjectWise : taskQuestionInfo
  )
    .sort((a, b) => a.sequence - b.sequence)
    .map((val) => val.questionId)
    .filter((val) => val !== undefined) as string[];

  const [activeQuestionId, setActiveQuestionId] = useState(
    sortedQuestionIds[0]
  );
  const { selected_resource_info } = deserify(
    useAppSelector((state) => state.learn)
  );
  const { isOffline } = useDownloadContext();

  const selectedHomeworkContent = activeHomeworkContent?.assessmentContent.find(
    (hw) => hw.questionId === activeQuestionId
  );
  const activeQuestionIndex = sortedQuestionIds.indexOf(activeQuestionId);
  const totalQuestions = sortedQuestionIds.length;
  const studentResponseForCurrentQuestion = new HWStudentResponse(
    activeHomeworkStudentResponse
  ).responses.find((resp) => resp.questionId === activeQuestionId);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const dispatch = useDispatch();
  const scorePercent = roundNumberTo2Decimal(
    ((activeHomeworkStudentResponse?.metrics?.responseScore || 0) * 100) /
      (activeHomeworkStudentResponse?.metrics?.maxScore || 0) || 0
  );

  const { setSelectedFunction } = useGlobalContext();
  const {
    LmsHomeworkCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  useEffect(() => {
    fetchHomeworkData();

    const redirectToHomeworkDashboard = async () => {
      navigate(HOMEWORK_STUDENT_DASHBOARD);
      await aiHomeworkReviewCloseEvent({
        homeworkId: Number(homework_id),
        isOffline: isOffline,
      });
    };
    setSelectedFunction(() => redirectToHomeworkDashboard);
    return () => {
      setSelectedFunction(null);
    };
  }, []);
  const handleSubjectTabsPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    if (!container) return;

    dragStateRef.current = {
      isPointerDown: true,
      isDragging: false,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
      pointerId: event.pointerId,
    };
  };

  const handleSubjectTabsPointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    if (!container) return;
    if (!dragStateRef.current.isPointerDown) return;
    if (dragStateRef.current.pointerId !== event.pointerId) return;

    if (event.pointerType === 'mouse' && event.buttons === 0) {
      dragStateRef.current.isPointerDown = false;
      dragStateRef.current.isDragging = false;
      dragStateRef.current.pointerId = -1;
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!dragStateRef.current.isDragging) {
      if (Math.abs(deltaX) < 5) return; // ignore tiny jitter — treat as click
      dragStateRef.current.isDragging = true;
      container.setPointerCapture(event.pointerId);
    }

    container.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
  };

  const handleSubjectTabsPointerUp = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = subjectTabsScrollRef.current;
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  };

  const handleSubjectTabsPointerLeave = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };

  const handleSubjectTabsPointerCancel = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };
  const fetchStudentAssessmentResponseByAttempt = async (
    selectedAttempt?: AttemptStats
  ) => {
    try {
      if (!selectedAttempt) {
        dispatch(
          setToastInfo({
            label: 'Something went wrong with assessment selection!',
            variant: 'error',
            open: true,
          })
        );
        return;
      }
      if (!studentId || !homework_id || isNaN(Number(homework_id))) {
        return;
      }
      const homeworkStudentResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
          {
            studentId: studentId,
            assessmentId: Number(homework_id),
            attemptNumber: selectedAttempt.attemptNumber,
          }
        );
      if (homeworkStudentResponse.data) {
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: homeworkStudentResponse.data,
          })
        );
        //changing the state in dropdown
        setSelectedAttemptInfo(selectedAttempt);
      } else {
        dispatch(
          setToastInfo({
            label: 'Something went wrong while fetching student response !',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      console.log(err);
      dispatch(
        setToastInfo({
          label: 'Something went wrong while fetching student response !',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const fetchHomeworkData = useCallback(async () => {
    if (!studentId || !homework_id || isNaN(Number(homework_id))) {
      return;
    }
    try {
      setLoading('loading');
      const homeworkContent =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: studentId,
            personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
            assessmentId: Number(homework_id),
          }
        );
      const ids =
        homeworkContent?.data?.questionsSequenceInfo?.questionIds || [];
      setActiveQuestionId(ids[0]);
      const homeworkStudentResponse =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
          {
            studentId: studentId,
            assessmentId: Number(homework_id),
          }
        );
      const assessmentData = updateAssessmentContentWithMarks(
        homeworkContent.data
      );
      if (homeworkFlow) {
        dispatch(
          setActiveHomeworkContent({
            homeworkId: Number(homework_id),
            homeworkContent: assessmentData,
          })
        );
        dispatch(
          setActiveHomeworkStudentResponse({
            homeworkId: Number(homework_id),
            response: homeworkStudentResponse.data,
          })
        );
      } else {
        dispatch(
          setActiveAssessmentContent({
            assessmentId: Number(homework_id),
            content: assessmentData,
          })
        );
        dispatch(
          setActiveAssessmentStudentResponse({
            assessmentId: Number(homework_id),
            response: homeworkStudentResponse.data,
          })
        );
      }

      if (homeworkStudentResponse.data?.attemptInfo) {
        const latestCompletedAttempt = homeworkStudentResponse.data.attemptInfo
          .filter(
            (e) =>
              e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          )
          .sort((a, b) => {
            return b.attemptNumber - a.attemptNumber;
          })[0];
        if (latestCompletedAttempt) {
          setSelectedAttemptInfo(latestCompletedAttempt);
        }
      }
      await aiHomeworkReviewOpenEvent({
        homeworkId: Number(homework_id),
        questionIds: ids,
        homeworkType: activeHomeworkContent?.assessment?.taskType,
        isOffline: isOffline,
      });

      dispatch(setSelectedHomeworkData({ homeworkId: Number(homework_id) }));

      setLoading('completed');
    } catch (err) {
      setLoading('error');
      dispatch(
        setActiveHomeworkContent({
          homeworkId: Number(homework_id),
          homeworkContent: undefined,
        })
      );
      dispatch(
        setActiveAssessmentContent({
          assessmentId: Number(homework_id),
          content: undefined,
        })
      );
      dispatch(
        setActiveHomeworkStudentResponse({
          homeworkId: Number(homework_id),
          response: undefined,
        })
      );
      dispatch(
        setActiveAssessmentStudentResponse({
          assessmentId: Number(homework_id),
          response: undefined,
        })
      );
      console.error(err);
    }
  }, [studentId, homework_id, setLoading, dispatch]);

  const navigate = useNavigate();

  const handlePageChange = async (page: number | null) => {
    if (page) {
      setActiveQuestionId(sortedQuestionIds[page - 1]);
    }
    await interactionEvent({
      url: 'Student_completed_homework',
      context: 'answer_&_explanation',
      name: 'QUESTION_SELECTION',
      isOffline: isOffline,
    });
  };

  useEffect(() => {
    if (multiSubjectSections.length > 0 && selectedSectionNo === undefined) {
      setSelectedSectionNo(multiSubjectSections[0].sectionNo);
    }
  }, [multiSubjectSections, selectedSectionNo]);

  useEffect(() => {
    if (sortedQuestionIds.length > 0) {
      setActiveQuestionId(sortedQuestionIds[0]);
    }
  }, [selectedSectionNo]);

  const getAnswerAttemptResult = (qId: string) => {
    const studentResponse = new HWStudentResponse(
      activeHomeworkStudentResponse
    ).responses.find((resp) => resp.questionId === qId);
    // If there's no response object at all
    if (
      !studentResponse ||
      !studentResponse.responses ||
      studentResponse.responses.length === 0
    ) {
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
    }
    //  clear-aware helper
    const historyState = getLastSignificantState(studentResponse.responses);
    // If the last significant intent was 'clear' or the history is empty
    if (historyState.status !== 'lastAnswered') {
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
    }
    // Otherwise, return the evaluation result from the server
    return studentResponse.isCorrect || QAttemptResultEnum.RESPONSE_UNDEFINED;
  };

  const questionStatusArray = sortedQuestionIds.map((qId, index) => {
    const attempt = getAnswerAttemptResult(qId);
    console.log({ qId, attempt });
    switch (attempt) {
      case QAttemptResultEnum.RESPONSE_CORRECT:
        return {
          index,
          statusInfo: IQuestionStatus.Correct,
        };
      case QAttemptResultEnum.RESPONSE_INCORRECT:
        return {
          index,
          statusInfo: IQuestionStatus.Incorrect,
        };
      case QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT:
        return {
          index,
          statusInfo: IQuestionStatus.Incorrect,
        };
      case QAttemptResultEnum.RESPONSE_UNDEFINED:
        return {
          index,
          statusInfo: IQuestionStatus.NotAttempted,
        };
    }
  });

  //^
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxMarks = activeHomeworkContent?.assessment?.maxMarks;
  const submissionMode = activeHomeworkContent?.assessment?.submissionType;

  const hiddenTypes = [
    TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED,
    TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM,
  ];
  return loading === 'loading' ? (
    <Loader />
  ) : loading === 'error' ? (
    <NoContentCard variant="error" icon="error" text="Error Occured" />
  ) : submissionMode === SubmissionType.SUBMISSION_OFFLINE ? (
    <OfflineEndedCustomHw />
  ) : submissionMode === SubmissionType.SUBMISSION_ONLINE ? (
    <CustomHomeworkDisplay />
  ) : (
    <Grid container spacing={2} sx={styles.root}>
      <Grid item xs={12} md={4} sx={styles.heading}>
        <>
          <Typography variant="h1">
            {activeHomeworkContent?.assessment?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED ||
            activeHomeworkContent?.assessment?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM
              ? 'Your Assessments'
              : activeHomeworkContent?.assessment?.assessmentType ===
                  AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT ||
                activeHomeworkContent?.assessment?.assessmentType ===
                  AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT
              ? `Your Assessment`
              : `Your Homework`}
          </Typography>
          {activeHomeworkContent?.assessment?.modeOfConduction ===
          ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER ? (
            <Box
              sx={{
                mt: { xs: pxToRem(5), md: pxTovW(20) },
              }}
            >
              <SelectMenu
                placeholder="select"
                value={
                  activeHomeworkStudentResponse?.attemptInfo?.length === 0
                    ? 'No Attempts'
                    : `Attempt ${selectedAttemptInfo?.attemptNumber}${
                        selectedAttemptInfo?.attemptType ===
                        AttemptTypeEnum.ATTEMPT_TYPE_BEST
                          ? ' (BEST)'
                          : ''
                      }`
                }
                disabled={
                  activeHomeworkStudentResponse?.attemptInfo?.length === 0
                }
                onChange={(value: string) => {
                  console.log('value', value, Number(value.split(' ')[1]));
                  fetchStudentAssessmentResponseByAttempt(
                    activeHomeworkStudentResponse?.attemptInfo?.find(
                      (item) =>
                        item.attemptNumber === Number(value.split(' ')[1])
                    )
                  );
                }}
                optionList={createIOptionListFromAttemptStats(
                  activeHomeworkStudentResponse?.attemptInfo
                )}
                rootStyles={{
                  width: { xs: '100%', md: pxTovW(400) },
                  height: { xs: pxToRem(55), md: pxTovW(75), lg: pxTovW(65) },
                  '& .Mui-disabled': { backgroundColor: '#F5FAFF' },
                }}
                selectedBold
                menuItemStyles={{
                  padding: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(20) },
                }}
              />
            </Box>
          ) : null}
          {
            <Box
              sx={{
                ...styles.subHeadingBox,
                // padding:
                //   maxMarks !== undefined || submissionMode === undefined
                //     ? {
                //         xs: `${pxToRem(14)} ${pxToRem(41)}`,
                //         md: `${pxTovW(36)} ${pxTovW(0)}`,
                //       }
                //     : {
                //         xs: `${pxToRem(14)} ${pxToRem(0)}`,
                //         md: `${pxTovW(36)} ${pxTovW(0)}`,
                //       },
              }}
            >
              {(maxMarks !== undefined || submissionMode === undefined) && (
                <Box sx={styles.subHeading}>
                  <Typography variant="g2" color="warning.main">
                    {Math.round(scorePercent)}%
                  </Typography>
                  <Typography variant="cardText" color="text.disabled">
                    Score
                  </Typography>
                </Box>
              )}
              <Box sx={styles.subHeading}>
                {/* {!(
                  activeHomeworkContent?.assessment?.taskType &&
                  hiddenTypes.includes(
                    activeHomeworkContent.assessment.taskType
                  )
                ) && (
                  <>
                    <Typography variant="h3">Learn better with this</Typography>
                    <LinkButton
                      onClick={async () => {
                        navigate(
                          `${HOMEWORK_VIEW_RECOMMENDATION}/${activeHomeworkContent?.assessment?.subjectId}/${activeHomeworkContent?.assessment?.chapterId}/${activeHomeworkContent?.assessment?.moduleId}/${activeHomeworkContent?.assessment?.taskType}`
                        );
                        await interactionEvent({
                          url: 'Student_completed_homework',
                          context: 'your_assessment',
                          name: 'VIEW_RECOMMENDATIONS',
                          isOffline: isOffline,
                        });
                        await aiHomeworkReviewCloseEvent({
                          homeworkId: Number(homework_id),
                          isOffline: isOffline,
                        });
                      }}
                    >
                      View Recommendations
                    </LinkButton>
                  </>
                )} */}
                {homework_id && studentId ? (
                  <ViewStudentReportButton
                    studentId={studentId}
                    homeworkId={Number(homework_id)}
                    attemptNumber={selectedAttemptInfo?.attemptNumber}
                    isJeeAssessment={
                      activeHomeworkContent?.assessment?.markingSchemeType ===
                      MarkingSchemeEnum.MARKING_SCHEME_JEE
                    }
                    isMultiSubjectAssessment={
                      activeHomeworkContent?.assessment?.taskType ===
                      TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM
                    }
                    subject={
                      activeHomeworkContent?.assessment?.subjects?.length
                        ? activeHomeworkContent.assessment.subjects.join('_')
                        : activeHomeworkContent?.assessment?.subject
                    }
                    className={activeHomeworkContent?.assessment?.class}
                    section={activeHomeworkContent?.assessment?.section}
                  />
                ) : null}
              </Box>
            </Box>
          }

          {activeHomeworkContent?.assessment?.taskType ===
            TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM &&
          (activeHomeworkStudentResponse as AssessmentStudentResponseV2)
            ?.multiSubjectMetrics?.length > 0 ? (
            <Box sx={styles.overallSummaryWrap}>
              <Typography variant="h2" fontWeight="bold">
                Overall Summary
              </Typography>

              <Box sx={styles.overallSummaryList}>
                {(
                  activeHomeworkStudentResponse as AssessmentStudentResponseV2
                ).multiSubjectMetrics.map((section) => (
                  <Box key={section.sectionNo} sx={styles.summaryCard}>
                    <Box sx={styles.summaryChip}>
                      <Typography variant="smallText" fontWeight={600}>
                        {`${getSubjectNameFromEnum(section.subject)}${
                          section.sectionTitle
                            ? ` - ${section.sectionTitle}`
                            : ''
                        }`}
                      </Typography>
                    </Box>

                    <Box sx={styles.summaryMetricsRow}>
                      <SummaryMetric
                        icon="clipboard-blue"
                        boldText={`${section.classSubjectScore}`}
                        text={`${section.classSubjectMaxScore}`}
                        label="Marks"
                      />
                      <SummaryMetric
                        icon="green-circle-check"
                        boldText={`${section.numberOfCorrectQuestions}`}
                        text={`${section.numberOfQuestions}`}
                        label="Correct"
                      />
                      <SummaryMetric
                        icon="clock"
                        boldText={`${section.timeDurationInMin}`}
                        label="Minutes"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </>
      </Grid>

      <Grid item xs={12} md={8} sx={{}}>
        <Box>
          <Box
            sx={{
              mb: { xs: pxToRem(20), md: pxTovW(20) },
              // paddingX: { md: pxTovW(20) },
            }}
          >
            <Typography variant="h2" fontWeight="bold">
              Answer & Explanations
            </Typography>
          </Box>
          <Box
            ref={subjectTabsScrollRef}
            onPointerDown={handleSubjectTabsPointerDown}
            onPointerMove={handleSubjectTabsPointerMove}
            onPointerUp={handleSubjectTabsPointerUp}
            onPointerLeave={handleSubjectTabsPointerLeave}
            onPointerCancel={handleSubjectTabsPointerCancel}
            sx={{
              display: 'block',
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
              cursor: 'grab',
              touchAction: 'pan-x',
              userSelect: 'none',

              '&:active': {
                cursor: 'grabbing',
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {activeHomeworkContent?.assessment?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM &&
            multiSubjectSections.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  width: 'max-content',
                  minWidth: '100%',
                  gap: { xs: pxToRem(8), md: pxTovW(20) },
                  px: { xs: pxToRem(6), md: pxTovW(12) },
                  py: { xs: pxToRem(10), md: pxTovW(10) },
                  backgroundColor: '#F3F9FE',
                  borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                  boxSizing: 'border-box',
                  '&::before, &::after': {
                    content: '""',
                    width: { xs: pxToRem(6), md: pxTovW(10) },
                    flex: '0 0 auto',
                  },
                }}
              >
                {multiSubjectSections.map((section) => {
                  const subject = `${getSubjectNameFromEnum(section.subject)}${
                    section.sectionTitle ? ` - ${section.sectionTitle}` : ''
                  }`;
                  const isSelected = section.sectionNo === selectedSectionNo;

                  return (
                    <Box
                      key={subject}
                      onClick={() => setSelectedSectionNo(section.sectionNo)}
                      sx={{
                        flex: '0 0 auto',
                        scrollSnapAlign: 'start',
                        height: { xs: pxToRem(32), md: pxTovW(50) },
                        minWidth: { xs: pxToRem(100), md: pxTovW(150) },
                        px: { xs: pxToRem(12), md: pxTovW(14) },
                        borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        backgroundColor: isSelected ? '#007CDC' : 'transparent',
                        color: isSelected ? '#fff' : '#000',
                        '&:hover': {
                          backgroundColor: isSelected ? '#007CDC' : '#E3F2FD',
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={isSelected ? 'bold' : 500}
                        color="inherit"
                        noWrap
                      >
                        {subject}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Box>
          <Box
            sx={{
              overflowX: 'scroll',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <HomeworkPagination
              currentPage={activeQuestionIndex + 1}
              totalPages={totalQuestions}
              showPreviousNextButtons={true}
              questionStatusArray={questionStatusArray}
              onPageChange={handlePageChange}
              maxButtonsToDisplay={10}
            />
          </Box>

          {/* Question Renderer */}
          <Box sx={styles.questionBox}>
            <QuestionContainerWithSolution
              key={activeQuestionIndex}
              disableInput
              isSubmitted
              showAnswer
              questionNumber={activeQuestionIndex + 1}
              question={new Question(selectedHomeworkContent)}
              userAttemptedAnswer={
                getLastSignificantState(
                  studentResponseForCurrentQuestion?.responses
                ).answer
              }
              currentResponse={studentResponseForCurrentQuestion}
              selected_resource_info={selected_resource_info}
              imageResizeEnabled={true}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
export interface IOptionList {
  name: string | number;
  id: number;
  nonEditableItem?: boolean;
  selectedNumber?: number;
}
export const createIOptionListFromAttemptStats = (
  attemptInfo?: AttemptStats[]
): IOptionList[] => {
  if (attemptInfo?.length) {
    return attemptInfo
      .map((data, index) => {
        const isBestAttempt =
          data.attemptType === AttemptTypeEnum.ATTEMPT_TYPE_BEST;
        return {
          name: `Attempt ${data.attemptNumber}${
            isBestAttempt ? ' (Best)' : ''
          }`,
          id: data.attemptNumber,
        };
      })
      .sort((a, b) => a.id - b.id);
  }

  return [
    {
      name: 'No Attempts',
      id: 0,
    },
  ];
};

interface ISummaryMetric {
  icon: string;
  boldText: string;
  text?: string;
  label: string;
}

const SummaryMetric = ({ icon, boldText, text, label }: ISummaryMetric) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: pxToRem(5), md: pxTovW(8) },
        }}
      >
        <IconWrapper name={icon} size="md" parentFolder="icons" type="png" />
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="h2" fontWeight="bold">
            {boldText}
          </Typography>
          {text ? (
            <Typography variant="h3" color="text.disabled">
              /{text}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Typography variant="h3" color="text.disabled">
        {label}
      </Typography>
    </Box>
  );
};
